<template>
  <router-view />
</template>

<script>
  export default {
    name: 'App',
    
    mounted() {
      // Initialize theme attribute on mount
      document.documentElement.setAttribute(
        'data-theme',
        this.$vuetify.theme.dark ? 'dark' : 'light'
      )
    }
  }
</script>

<style lang="scss">
/* ============================================================
   Import Brand Theme System
   ============================================================ */
@import '@/sass/theme.scss';

/* ============================================================
   ElevMent Premium Theme - Global Styles
   Apple/SaaS Inspired with Brand Orange
   Light + Dark Mode Support
   ============================================================ */

/* --- Base Reset & Font --- */
*,
*::before,
*::after {
  box-sizing: border-box;
}

html, body {
  font-family: 'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* --- Light Mode Base --- */
.theme--light html, .theme--light body,
html, body {
  color: #1D1D1F;
  background-color: #F5F5F7;
}

.theme--light.v-application {
  font-family: 'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif !important;
  background-color: #F5F5F7 !important;
}

/* --- Dark Mode Base --- */
.theme--dark html, .theme--dark body {
  color: #FFFFFF;
  background-color: #0D0D0D; /* ANTES: #121212 */
}

.theme--dark.v-application {
  font-family: 'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif !important;
  background-color: #0D0D0D !important; /* ANTES: #121212 */
  color: #FFFFFF !important;
}

.theme--dark .v-main,
.theme--dark .v-main__wrap,
.theme--dark .v-content {
  background-color: #0D0D0D !important;
}

/* --- Headings Font --- */
.v-application .text-h1,
.v-application .text-h2,
.v-application .text-h3,
.v-application .text-h4,
.v-application .text-h5,
.v-application .text-h6 {
  font-family: 'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif !important;
}

/* ============================================================
   LAYOUT - Margens laterais aumentadas
   ============================================================ */
.v-main > .v-main__wrap {
  padding-left: 48px !important;
  padding-right: 48px !important;
}

@media (max-width: 960px) {
  .v-main > .v-main__wrap {
    padding-left: 24px !important;
    padding-right: 24px !important;
  }
}

@media (max-width: 600px) {
  .v-main > .v-main__wrap {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}

/* ============================================================
   CARDS
   ============================================================ */

/* Light */
.theme--light .v-card:not(.v-card--flat) {
  border: 1px solid #E5E5E7;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  background-color: #FFFFFF !important;
  border-radius: 12px !important;
}

.theme--light .v-card.v-card--flat {
  box-shadow: none !important;
  border: 1px solid #E5E5E7;
  background-color: #FFFFFF !important;
  border-radius: 12px !important;
}

/* Dark */
.theme--dark .v-app-bar.v-app-bar--fixed {
  border-bottom: 1px solid #222222 !important; /* ANTES: #38383A - Borda mais sutil no fundo preto */
  box-shadow: none !important; /* Removemos a sombra para um look mais clean no fundo preto, ou mantenha se preferir */
  background-color: #0D0D0D !important; /* ANTES: #1C1C1E */
}

.theme--dark .v-card.v-card--flat {
  box-shadow: none !important;
  border: 1px solid #38383A;
  background-color: #1C1C1E !important;
  border-radius: 12px !important;
}

/* Card hover effect with glow in dark mode */
.theme--dark .v-card:hover:not(.v-card--flat) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 119, 28, 0.15) !important;
}

/* ============================================================
   DIVIDERS
   ============================================================ */
.theme--light .v-divider {
  border-color: #E5E5E7 !important;
}

.theme--dark .v-divider {
  border-color: #38383A !important;
}

/* ============================================================
   PROGRESS LINEAR
   ============================================================ */
.v-progress-linear {
  border-radius: 8px !important;
  overflow: hidden;
}

.theme--light .v-progress-linear__background {
  opacity: 0.15 !important;
}

.theme--dark .v-progress-linear__background {
  opacity: 0.2 !important;
}

/* ============================================================
   AVATARS
   ============================================================ */
.v-avatar {
  border-radius: 12px !important;
}

.v-avatar.rounded-circle,
.v-list-item__avatar .v-avatar {
  border-radius: 50% !important;
}

/* ============================================================
   TOOLTIPS
   ============================================================ */
.v-tooltip__content {
  background: #111827 !important;
  color: #F9FAFB !important;
  font-size: 12px !important;
  padding: 6px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* ============================================================
   MENUS
   ============================================================ */
.theme--light .v-menu__content {
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #E5E5E7 !important;
}

.theme--dark .v-menu__content {
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #38383A !important;
  background-color: #1C1C1E !important;
}

/* ============================================================
   SNACKBAR
   ============================================================ */
.v-snack__wrapper {
  border-radius: 12px !important;
}

/* ============================================================
   APP BAR
   ============================================================ */
/* Light */
.theme--light .v-app-bar.v-app-bar--fixed {
  border-bottom: 1px solid #E5E5E7 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
  background-color: #FFFFFF !important;
}

/* Dark */
.theme--dark .v-app-bar.v-app-bar--fixed {
  border-bottom: 1px solid #222222 !important;
  box-shadow: none !important;
  background-color: #000000 !important;
}

/* ============================================================
   SWITCH
   ============================================================ */
.v-input--switch .v-input--switch__track {
  border-radius: 12px !important;
}

.v-input--switch .v-input--switch__thumb {
  border-radius: 50% !important;
}

/* ============================================================
   DIALOGS
   ============================================================ */
.theme--dark .v-dialog .v-card {
  background-color: #1C1C1E !important;
  border: 1px solid #38383A !important;
}

/* ============================================================
   DATA TABLES
   ============================================================ */
.theme--dark .v-data-table {
  background-color: #1C1C1E !important;
}

.theme--dark .v-data-table thead th {
  background-color: #121212 !important;
  color: #AEAEB2 !important;
}

.theme--dark .v-data-table tbody td {
  color: #FFFFFF !important;
  border-color: #38383A !important;
}

.theme--dark .v-data-table tbody tr:hover {
  background-color: rgba(255, 119, 28, 0.08) !important;
}

/* ============================================================
   TABS
   ============================================================ */
.theme--dark .v-tabs {
  background-color: #1C1C1E !important;
}

.theme--dark .v-tabs .v-tab {
  color: #AEAEB2 !important;
}

.theme--dark .v-tabs .v-tab--active {
  color: #FF771C !important;
}

.theme--dark .v-tabs-items {
  background-color: transparent !important;
}

/* ============================================================
   SHEETS & EXPANSION PANELS
   ============================================================ */
.theme--dark .v-sheet {
  background-color: #1C1C1E !important;
}

.theme--dark .v-expansion-panel {
  background-color: #1C1C1E !important;
  border-color: #38383A !important;
}

.theme--dark .v-expansion-panel::before {
  box-shadow: none !important;
}

/* ============================================================
   TEXT FIELDS (Dark)
   ============================================================ */
.theme--dark .v-text-field--outlined fieldset {
  border-color: #38383A !important;
}

.theme--dark .v-text-field--outlined:hover fieldset {
  border-color: #48484A !important;
}

.theme--dark .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #FF771C !important;
}

.theme--dark .v-text-field--filled .v-input__slot {
  background: #121212 !important;
}

.theme--dark .v-text-field input,
.theme--dark .v-text-field textarea {
  color: #FFFFFF !important;
}

.theme--dark .v-label {
  color: #AEAEB2 !important;
}

.theme--dark .v-input--is-focused .v-label {
  color: #FF771C !important;
}

/* ============================================================
   LISTS (Dark)
   ============================================================ */
.theme--dark .v-list {
  background-color: #1C1C1E !important;
}

.theme--dark .v-list-item:hover::before {
  background-color: rgba(255, 119, 28, 0.08) !important;
}

.theme--dark .v-list-item__title {
  color: #FFFFFF !important;
}

.theme--dark .v-list-item__subtitle {
  color: #AEAEB2 !important;
}

/* ============================================================
   CHIPS (Dark)
   ============================================================ */
.theme--dark .v-chip {
  background-color: #2C2C2E !important;
  color: #FFFFFF !important;
}

.theme--dark .v-chip.v-chip--outlined {
  border-color: #38383A !important;
}

/* ============================================================
   AUTOCOMPLETE & SELECT (Dark)
   ============================================================ */
.theme--dark .v-select .v-select__selection {
  color: #FFFFFF !important;
}

.theme--dark .v-autocomplete .v-input__slot {
  background-color: #121212 !important;
}

/* ============================================================
   ANIMATIONS
   ============================================================ */
* {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* ============================================================
   UTILITY CLASSES
   ============================================================ */
.premium-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 24px;
  transition: all 0.3s ease;
}

.theme--light .premium-card {
  background: #FFFFFF;
  border: 1px solid #E5E5E7;
}

.theme--dark .premium-card {
  background: #1C1C1E;
  border: 1px solid #38383A;
}

.theme--dark .premium-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 119, 28, 0.15);
}

.text-primary-dark {
  color: #1D1D1F !important;
}

.theme--dark .text-primary-dark {
  color: #FFFFFF !important;
}

.text-secondary-muted {
  color: #86868B !important;
}

.theme--dark .text-secondary-muted {
  color: #AEAEB2 !important;
}

.bg-surface {
  background-color: #FFFFFF !important;
}

.theme--dark .bg-surface {
  background-color: #1C1C1E !important;
}

.bg-page {
  background-color: #F5F5F7 !important;
}

.theme--dark .bg-page {
  background-color: #000000 !important; /* ANTES: #121212 */
}

.border-subtle {
  border: 1px solid #E5E5E7 !important;
}

.theme--dark .border-subtle {
  border: 1px solid #38383A !important;
}

.shadow-subtle {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
}

.theme--dark .shadow-subtle {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
}

.rounded-premium {
  border-radius: 12px !important;
}

/* Brand specific utilities */
.brand-gradient {
  background: linear-gradient(135deg, #FF771C, #F6AB2E) !important;
  color: #FFFFFF !important;
}

.brand-text-gradient {
  background: linear-gradient(135deg, #FF771C, #F6AB2E);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.theme--dark .glow-effect {
  box-shadow: 0 0 20px rgba(255, 119, 28, 0.3) !important;
}

.theme--dark .glow-effect-strong {
  box-shadow: 0 0 30px rgba(255, 119, 28, 0.5) !important;
}

/* ============================================================
   DARK MODE TRANSITION
   ============================================================ */
.v-application,
.v-card,
.v-sheet,
.v-app-bar,
.v-navigation-drawer,
.v-data-table,
.v-tabs,
.v-list,
.v-expansion-panel,
.v-text-field .v-input__slot,
.v-menu__content {
  transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease !important;
}
</style>
